import { apiClient } from '@/lib/api/client';

// ── Bedrock response envelope ───────────────────────────────────────────────
//
// Every Bedrock endpoint returns BedrockResponse<T> = { success, code, data, errors }.
// Service functions read response.data.data and surface response.data.code on errors.

export interface BedrockEnvelope<T> {
  success: boolean;
  code: string | null;
  data: T;
  errors: string[] | null;
}

// ── Public result types (Bedrock-native shapes) ─────────────────────────────

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  accessTokenExpiresAt: string;
}

export interface LoginResult {
  accessToken: string | null;
  refreshToken: string | null;
  accessTokenExpiresAt: string | null;
  requiresMfa: boolean;
  challengeToken: string | null;
  challengeMethod: 'Totp' | 'EmailOtp' | 'SmsOtp' | null;
  challengeExpiresAt: string | null;
  requiresEnrollment: boolean;
  enrollmentToken: string | null;
  mfaGracePeriodEndsAt: string | null;
}

export interface TotpSetupResult {
  qrUri: string;
}

export interface RecoveryCodesResult {
  recoveryCodes: string[];
}

export interface StepUpChallenge {
  challengeId: string;
  method: 'Totp' | 'EmailOtp' | 'SmsOtp';
}

export interface StepUpToken {
  stepUpToken: string;
}

// ── Wire-shape helpers (private) ────────────────────────────────────────────
//
// Bedrock's RecoveryCodesResponse is { codes: string[] }; we expose
// { recoveryCodes } to consumers for self-documenting field names.

interface RecoveryCodesWire {
  codes: string[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

export async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

// ── Anonymous endpoints ─────────────────────────────────────────────────────

export async function login(email: string, password: string): Promise<LoginResult> {
  const response = await apiClient.post<BedrockEnvelope<LoginResult>>('/auth/login', {
    email,
    password,
  });
  return response.data.data;
}

export async function verifyMfa(challengeToken: string, code: string): Promise<TokenPair> {
  const response = await apiClient.post<BedrockEnvelope<TokenPair>>('/auth/verify-2fa', {
    challengeToken,
    code,
  });
  return response.data.data;
}

export async function refresh(refreshToken: string): Promise<TokenPair> {
  const response = await apiClient.post<BedrockEnvelope<TokenPair>>('/auth/refresh', {
    refreshToken,
  });
  return response.data.data;
}

export async function revoke(refreshToken: string): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/auth/revoke', { refreshToken });
}

export async function forgotPassword(email: string): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/auth/forgot-password', { email });
}

export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/auth/reset-password', { token, newPassword });
}

export async function acceptInvite(rawToken: string, password: string): Promise<TokenPair> {
  const tokenHash = await sha256Hex(rawToken);
  const response = await apiClient.post<BedrockEnvelope<TokenPair>>('/auth/accept-invitation', {
    tokenHash,
    password,
  });
  return response.data.data;
}

// ── Authenticated endpoints ─────────────────────────────────────────────────

export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/auth/change-password', {
    currentPassword,
    newPassword,
  });
}

// ── MFA enrolment ───────────────────────────────────────────────────────────

function bearerConfig(token: string | undefined) {
  return token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
}

export async function requestEnrollment(): Promise<string> {
  const response = await apiClient.post<BedrockEnvelope<{ enrollmentToken: string }>>(
    '/auth/request-enrollment',
    {},
  );
  return response.data.data.enrollmentToken;
}

export async function enableMfa(enrollmentToken?: string): Promise<TotpSetupResult> {
  const response = await apiClient.post<BedrockEnvelope<TotpSetupResult>>(
    '/2fa/setup-totp',
    {},
    bearerConfig(enrollmentToken),
  );
  return response.data.data;
}

export async function confirmMfaEnrollment(
  totpCode: string,
  enrollmentToken?: string,
): Promise<RecoveryCodesResult> {
  const response = await apiClient.post<BedrockEnvelope<RecoveryCodesWire>>(
    '/2fa/confirm-totp',
    { code: totpCode },
    bearerConfig(enrollmentToken),
  );
  return { recoveryCodes: response.data.data.codes };
}

// ── Step-up-protected endpoints ─────────────────────────────────────────────

function stepUpHeaders(stepUpToken: string) {
  return { headers: { 'X-Step-Up-Token': stepUpToken } };
}

export async function disableMfa(stepUpToken: string): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/2fa/disable', {}, stepUpHeaders(stepUpToken));
}

export async function regenerateRecoveryCodes(stepUpToken: string): Promise<RecoveryCodesResult> {
  const response = await apiClient.post<BedrockEnvelope<RecoveryCodesWire>>(
    '/2fa/recovery-codes/regenerate',
    {},
    stepUpHeaders(stepUpToken),
  );
  return { recoveryCodes: response.data.data.codes };
}

// ── Step-up flow ────────────────────────────────────────────────────────────

export async function initiateStepUp(): Promise<StepUpChallenge> {
  const response = await apiClient.post<BedrockEnvelope<StepUpChallenge>>(
    '/step-up/initiate',
    {},
  );
  return response.data.data;
}

export async function verifyStepUp(challengeId: string, code: string): Promise<StepUpToken> {
  const response = await apiClient.post<BedrockEnvelope<StepUpToken>>('/step-up/verify', {
    challengeId,
    code,
  });
  return response.data.data;
}

// ── Admin: invitations ──────────────────────────────────────────────────────

export async function invite(targetEmail: string, roleHint?: string): Promise<void> {
  await apiClient.post<BedrockEnvelope<null>>('/admin/invitations', {
    targetEmail,
    roleHint,
  });
}
